/**
 * Prediction-First P0 — Guest micro-prediction storage.
 *
 * Stores a pre-signup prediction in localStorage so a brand-new visitor
 * can answer "next play" (Gol / Tiro / Córner) in a single tap, before
 * touching Auth, the Prediction Engine, RLS or Scoring.
 *
 * Nothing in this module talks to Supabase, the Prediction Engine, the
 * Nation Wars scorer or Realtime — it is pure client-side state used to
 * compress TTFP under 15 seconds.
 */
import { trackEvent } from "@/lib/audit";
import { getDeviceHash } from "@/lib/antifraud/deviceFingerprint";
import { recordDeviceSignal, claimGuestPredictionXp } from "@/lib/antifraud/antifraud.functions";

export const GUEST_PREDICTION_KEY = "vs_pending_prediction";
export const FIRST_ENGAGEMENT_KEY = "vs_first_engagement_done";
export const PENDING_XP_AMOUNT = 25;

export type GuestPredictionChoice = "goal" | "shot" | "corner";

export interface PendingPrediction {
  choice: GuestPredictionChoice;
  at: number;
  pending_xp: number;
  suggested_nation: string | null;
}

export function readPendingPrediction(): PendingPrediction | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(GUEST_PREDICTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPrediction;
    if (!parsed?.choice) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function savePendingPrediction(
  choice: GuestPredictionChoice,
  suggestedNation: string | null,
) {
  if (typeof window === "undefined") return;
  const payload: PendingPrediction = {
    choice,
    at: Date.now(),
    pending_xp: PENDING_XP_AMOUNT,
    suggested_nation: suggestedNation,
  };
  try {
    window.localStorage.setItem(GUEST_PREDICTION_KEY, JSON.stringify(payload));
    window.localStorage.setItem(FIRST_ENGAGEMENT_KEY, "1");
  } catch {
    /* noop */
  }
  void trackEvent("guest_prediction_completed", {
    choice,
    suggested_nation: suggestedNation,
    pending_xp: PENDING_XP_AMOUNT,
  });
  // PR-34B — privacy-safe device signal (best-effort, never blocks UX).
  void (async () => {
    try {
      const deviceHash = await getDeviceHash();
      if (!deviceHash) return;
      await recordDeviceSignal({
        data: {
          deviceHash,
          kind: "guest_prediction",
          metadata: { choice, suggested_nation: suggestedNation },
        },
      });
    } catch { /* noop */ }
  })();
}

export function clearPendingPrediction() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(GUEST_PREDICTION_KEY);
  } catch {
    /* noop */
  }
}

export function hasFirstEngagement(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(FIRST_ENGAGEMENT_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * PR-30 — Claim the guest's pending prediction once they finish signup.
 * Fires the conversion events, clears the localStorage payload and
 * returns the original prediction so callers can surface a toast like
 * "Tu predicción fue guardada · +25 XP". Safe to call multiple times —
 * subsequent calls are no-ops.
 */
export function claimPendingPrediction(): PendingPrediction | null {
  const pending = readPendingPrediction();
  if (!pending) return null;
  void trackEvent("pending_prediction_claimed", {
    choice: pending.choice,
    pending_xp: pending.pending_xp,
    suggested_nation: pending.suggested_nation,
    age_ms: Date.now() - pending.at,
  });
  void trackEvent("guest_prediction_converted", {
    choice: pending.choice,
    suggested_nation: pending.suggested_nation,
  });
  clearPendingPrediction();
  // P0-HOTFIX-01 / P0-4 — Actually grant the pending XP via the
  // claim_guest_prediction_xp RPC. Previous behaviour only fired audit
  // events, so xp_grant_log never received the +25 XP and Guest Claim
  // never converted. Best-effort, idempotent per (user, choice, at).
  void (async () => {
    try {
      const deviceHash = await getDeviceHash();
      if (!deviceHash) return;
      const idempotencyKey = `guest_claim:${pending.choice}:${pending.at}`;
      await claimGuestPredictionXp({ data: { deviceHash, idempotencyKey } });
    } catch { /* noop — auth/network errors must not break the UX */ }
  })();
  return pending;
}

/**
 * PR-30 — Read the geo-suggested nation the guest accepted (implicitly)
 * during the micro-prediction. NationGate uses this to skip the full
 * picker grid in favor of a 1-click confirm.
 */
export function readSuggestedNation(): string | null {
  const pending = readPendingPrediction();
  return pending?.suggested_nation ?? null;
}

/**
 * Map geo-IP country code → suggested World Cup nation code used across
 * `worldcup_teams`. Only covers the P0 priority markets; anything else
 * returns null so the user picks manually.
 */
export function suggestNationFromGeo(country: string | null | undefined): string | null {
  if (!country) return null;
  const c = country.toUpperCase();
  switch (c) {
    case "BR": return "BRA";
    case "MX": return "MEX";
    case "AR": return "ARG";
    case "IN": return "IND";
    default: return null;
  }
}

export function suggestedNationLabel(code: string | null): { flag: string; name: string } | null {
  switch (code) {
    case "BRA": return { flag: "🇧🇷", name: "Brasil" };
    case "MEX": return { flag: "🇲🇽", name: "México" };
    case "ARG": return { flag: "🇦🇷", name: "Argentina" };
    case "IND": return { flag: "🇮🇳", name: "India" };
    default: return null;
  }
}